import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TimelineItemComponent } from '../timeline-item/timeline-item.component';
import { TimelineCardComponent } from '../timeline-card/timeline-card.component';
import { CommonModule } from '@angular/common';
import { CompactTimeline, Timeline } from '../../../interfaces/timeline';
import { MatchingPeriod } from '../../../interfaces/matchingPeriod';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-timeline-entry[compactTimelineEntrys]',
  imports: [TimelineItemComponent, TimelineCardComponent, CommonModule],
  templateUrl: './timeline-entry.component.html',
  styleUrl: './timeline-entry.component.scss'
})
export class TimelineEntryComponent {
  @Input() compactTimelineEntrys!: CompactTimeline[];
  @Input() currentMatchingPeriod!: MatchingPeriod | null;
  @Input() nextMatchingPeriod!: MatchingPeriod | null;

  expandedStates: { [key: string]: boolean } = {}; // Store expanded state per item

  onExpandedChange(expanded: boolean, index: number) {
    this.expandedStates[index] = expanded; // Update the state for this particular item
  }

  getTimelineTitle(compactTimeline: CompactTimeline): string {
    //console.log(compactTimeline);
    if (compactTimeline.enissync) {
      return 'Uploaded ENIS extract';
    } else if (compactTimeline.crossmatchpositive) {
      return `Positive Crossmatches at ${compactTimeline.centerName}`;
    } else if (compactTimeline.crossmatchexcluded) {
      return `Excluded Combinations at ${compactTimeline.centerName}`;
    } else if (compactTimeline.crossmatchnegative) {
      return `Negative Crossmatches at ${compactTimeline.centerName}`;
    } else if (compactTimeline.crossmatchremoved) {
      return `Removed Crossmatches at ${compactTimeline.centerName}`;
    } else if (compactTimeline.matchruns) {
      return `Match run #${compactTimeline.matchruns}`;
    } else if (compactTimeline.chainlocked) {
      return `Chain${compactTimeline.chainlocked > 1 ? 's' : ''} Locked`;
    } else if (compactTimeline.chainunlocked) {
      return `Chain${compactTimeline.chainunlocked > 1 ? 's' : ''} Unlocked`;
    } else if (compactTimeline.selectedfortransplantation) {
      return `Chain${compactTimeline.selectedfortransplantation > 1 ? 's' : ''} Selected for Transplantation`;
    } else if (compactTimeline.transplanted) {
      return `Chain${compactTimeline.transplanted > 1 ? 's' : ''} Transplanted`;
    }
    return `Participant changes by ${compactTimeline.centerName}`;
  }

  getTimelineIcon(compactTimeline: CompactTimeline): string {
    if (compactTimeline.enissync) {
      return 'import_export';
    } else if (compactTimeline.crossmatchpositive) {
      return 'add';
    } else if (compactTimeline.crossmatchexcluded) {
      return 'block';
    } else if (compactTimeline.crossmatchnegative) {
      return 'add';
    } else if (compactTimeline.crossmatchremoved) {
      return 'cancel';
    } else if (compactTimeline.matchruns) {
      return 'autorenew';
    } else if (compactTimeline.chainlocked) {
      return 'lock';
    } else if (compactTimeline.chainunlocked) {
      return 'lock_open';
    } else if (compactTimeline.selectedfortransplantation) {
      return 'check_circle';
    } else if (compactTimeline.transplanted) {
      return 'verified';
    }
    return 'group';
  }

  getTimelineLevel(compactTimeline: CompactTimeline): 'info' | 'success' | 'fail' | 'default' | 'status' | 'end' {
    if (compactTimeline.enissync) {
      return 'info'; // Blue for ENIS sync
    } else if (compactTimeline.crossmatchpositive) {
      return 'fail'; // Positive crossmatch = incompatible = red
    } else if (compactTimeline.crossmatchexcluded || compactTimeline.crossmatchremoved) {
      return 'info';
    } else if (compactTimeline.crossmatchnegative) {
      return 'success'; // Negative crossmatch = compatible = green
    } else if (compactTimeline.matchruns) {
      return 'default';
    } else if (compactTimeline.chainlocked) {
      return 'info'; // Blue for locked chains
    } else if (compactTimeline.chainunlocked) {
      return 'default'; // Gray for unlocked chains
    } else if (compactTimeline.selectedfortransplantation) {
      return 'success'; // Green for selected for transplantation
    } else if (compactTimeline.transplanted) {
      return 'success'; // Green for transplanted
    }
    return 'status';
  }

  isTimelineInverted(compactTimeline: CompactTimeline): boolean {
    // ENIS sync should be on the lef
    if (compactTimeline.enissync) {
      return false;
    }

    return !(
      compactTimeline.crossmatchpositive ||
      compactTimeline.crossmatchexcluded ||
      compactTimeline.crossmatchnegative ||
      compactTimeline.crossmatchremoved ||
      compactTimeline.matchruns ||
      compactTimeline.chainlocked ||
      compactTimeline.chainunlocked ||
      compactTimeline.selectedfortransplantation ||
      compactTimeline.transplanted
    );
  }

  isExpandable(compactTimeline: CompactTimeline): boolean {
    return !compactTimeline.matchruns && !compactTimeline.enissync;
  }

  isDeeplink(compactTimeline: CompactTimeline): string | undefined {
    if (compactTimeline.matchruns) {
      return `/matchrun/${this.currentMatchingPeriod?.matchingPeriodName}/${compactTimeline.matchruns}`;
    }
    return;
  }
}
